import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip } from '@mui/material';
import { useMemo, useState } from 'react';

type Material = { id: number; name: string };

interface MaterialFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (selectedMaterialIds: number[], selectedElements: string[]) => void;
  selectedMaterialIds: number[];
  selectedElements: string[];
  allMaterials: Material[];
}

// Periodic Table layout (simplified for chips, left-padding as empty strings)
const PERIODIC_TABLE_LAYOUT: string[][] = [
  ['H', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'He'],
  ['Li', 'Be', '', '', '', '', '', '', '', '', '', '', 'B', 'C', 'N', 'O', 'F', 'Ne'],
  ['Na', 'Mg', '', '', '', '', '', '', '', '', '', '', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar'],
  ['K', 'Ca', 'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn', 'Ga', 'Ge', 'As', 'Se', 'Br', 'Kr'],
  ['Rb', 'Sr', 'Y', 'Zr', 'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd', 'In', 'Sn', 'Sb', 'Te', 'I', 'Xe'],
  ['Cs', 'Ba', 'La', 'Hf', 'Ta', 'W', 'Re', 'Os', 'Ir', 'Pt', 'Au', 'Hg', 'Tl', 'Pb', 'Bi', 'Po', 'At', 'Rn'],
  ['Fr', 'Ra', 'Ac', 'Th', 'Pa', 'U', 'Np', 'Pu', 'Am', 'Cm', 'Bk', 'Cf', 'Es', 'Fm', 'Md', 'No', 'Lr'],
  ['', '', '', 'Ce', 'Pr', 'Nd', 'Pm', 'Sm', 'Eu', 'Gd', 'Tb', 'Dy', 'Ho', 'Er', 'Tm', 'Yb', 'Lu', ''],
];

export const MaterialFilterDialog = ({
  open,
  onClose,
  onApply,
  allMaterials,
  selectedMaterialIds: materialsFromFilter,
  selectedElements: elementsFromFilter,
}: MaterialFilterDialogProps) => {
  const [selectedElements, setSelectedElements] = useState<string[]>(elementsFromFilter);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>(materialsFromFilter);

  const usedElementSet = useMemo(() => {
    const elementRegex = /[A-Z][a-z]?/g;
    const used = new Set<string>();
    allMaterials.forEach((mat) => {
      const matches = mat.name.match(elementRegex);
      if (matches) matches.forEach((el) => used.add(el));
    });
    return used;
  }, [allMaterials]);

  const filteredMaterials = useMemo(() => {
    const elementRegex = /[A-Z][a-z]?/g;
    if (selectedElements.length === 0) return allMaterials;
    return allMaterials.filter((mat) => {
      const matches = (mat.name.match(elementRegex) ?? []) as string[];
      return selectedElements.every((el) => matches.includes(el));
    });
  }, [allMaterials, selectedElements]);

  // Elements that keep at least one material available when added to current selection
  const availableElementSet = useMemo(() => {
    const elementRegex = /[A-Z][a-z]?/g;
    // If nothing selected, any element that appears in any material is allowed
    if (selectedElements.length === 0) return usedElementSet;

    const allowed = new Set<string>();
    for (const mat of allMaterials) {
      const matches = (mat.name.match(elementRegex) ?? []) as string[];
      // Only consider materials that include all currently selected elements
      const includesAllSelected = selectedElements.every((el) => matches.includes(el));
      if (includesAllSelected) {
        for (const el of matches) {
          allowed.add(el);
        }
      }
    }
    // Always allow already-selected elements so user can deselect
    selectedElements.forEach((el) => allowed.add(el));

    return allowed;
  }, [allMaterials, selectedElements, usedElementSet]);

  const toggleElement = (el: string) => {
    setSelectedElements((prev) => (prev.includes(el) ? prev.filter((e) => e !== el) : [...prev, el]));
  };

  const toggleMaterial = (id: number) => {
    setSelectedMaterialIds((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  };

  const resetElements = () => {
    setSelectedElements([]);
    setSelectedMaterialIds([]);
  };

  const handleApply = () => {
    // If one or more elements are selected and no explicit materials chosen,
    // interpret as "Element(s) + *" → pass elements array and empty materials array
    if (selectedElements.length > 0 && selectedMaterialIds.length === 0) {
      onApply([], selectedElements);
      onClose();
      return;
    }

    onApply(selectedMaterialIds, []);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Filter by Element and Material</DialogTitle>
      <DialogContent dividers>
        <Typography fontWeight={600} gutterBottom>
          Elements
        </Typography>
        <Box display="flex" justifyContent="center" mb={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(18, 36px)',
              gap: 0.5,
            }}
          >
            {PERIODIC_TABLE_LAYOUT.flat().map((el, idx) =>
              el === '' ? (
                <Box key={`empty-${idx}`} sx={{ width: 36, height: 32 }} />
              ) : (
                <Chip
                  key={el}
                  label={el}
                  clickable={availableElementSet.has(el)}
                  onClick={() => toggleElement(el)}
                  color={selectedElements.includes(el) ? 'primary' : 'default'}
                  size="small"
                  sx={{
                    opacity: availableElementSet.has(el) ? 1 : 0.3,
                    pointerEvents: availableElementSet.has(el) ? 'auto' : 'none',
                    width: 36,
                    height: 32,
                    justifyContent: 'center',
                  }}
                />
              )
            )}
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={3}>
          <Button onClick={resetElements} variant="outlined" color="error" size="large">
            Reset
          </Button>
        </Box>

        <Typography fontWeight={600} gutterBottom>
          Materials
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} maxHeight={200} overflow="auto">
          {filteredMaterials.map((mat) => (
            <Chip
              key={mat.id}
              label={mat.name}
              clickable
              onClick={() => toggleMaterial(mat.id)}
              color={selectedMaterialIds.includes(mat.id) ? 'primary' : 'default'}
              size="small"
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="large" variant="outlined" color="error">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" size="large">
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
  );
};
